// File: frontend/src/pages/profile/ProfilePage.tsx
// src/pages/profile/ProfilePage.tsx

import React, { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ProfileModel, EntryModel } from "../../models";
import { 
  fetchProfileById, 
  fetchPublicProfileEntries, 
  fetchPrivateProfileEntries,
  adminRemovePin,
  updateBio,
  uploadAvatar,
  deleteAvatar,
  uploadBanner,
  deleteBanner,
  fetchUserTopicSuggestions,
  UserTopicSuggestionItem,
  PublicProfileTab, 
  PrivateProfileTab 
} from "../../services/profileService";
import { cancelAccountDeletion } from "../../services/accountDeletionService";
import { getMyRookieRank } from "../../services/rookieRankService";
import { RookieRankDto } from "../../types/rookie.types";
import {
  adminEditUserBio,
  adminDeleteUserBio,
  adminBlockUserBio,
  adminUnblockUserBio,
} from "../../services/adminUserService";
import { ACCOUNT_DELETION_CANCEL_COOLDOWN_HOURS, BIO_MAX_LENGTH } from "../../constants";
import { useCooldownTimer } from "../../hooks/useCooldownTimer";
import { SpinnerLoading, Pagination, generateMatchUrlFromEntry } from "../../utils";
// ProfileEntryAccordion stays in components/profile — import by full path
import { ProfileEntryAccordion, VoteHighlight } from "../../components/profile/ProfileEntryAccordion";
// EntryRow stays in components/entries — import by full path
import { EntryRow } from "../../components/entries/EntryRow";
import { useAuth } from "../../context/AuthContext";
import {
  RiPushpinLine,
  RiCloseLine,
  RiDragMove2Line,
  RiEditLine,
  RiCheckLine,
  RiCameraLine,
  RiVerifiedBadgeFill,
} from "react-icons/ri";
import ImageCropModal, { CropArea } from "../../components/profile/ImageCropModal";
import {
  FaEdit,
  FaTrash,
  FaBan,
  FaUndo,
} from "react-icons/fa";
import { getStatusCssClass, getRoleCssClass, getRoleI18nKey, getStatusTooltip } from "../../utils/userStatusUtils";
import FollowButton from "../../components/profile/FollowButton";
import BlockButton from "../../components/profile/BlockButton";
import MessageButton from "../../components/profile/MessageButton";
import FollowModal, { FollowModalTab } from "../../components/profile/FollowModal";
// Album grid ("Albüm" main tab) — owns its own loading and pagination
import { ProfileAlbum } from "../../components/profile/ProfileAlbum";
import { fetchFollowing, followUser, unfollowUser } from "../../services/followService";
import { fetchBlockedList, unblockUser, BlockedUserItem } from "../../services/blockService";
import { FollowUserModel } from "../../models/FollowModel";
import "./ProfilePage.css";
import "../../components/profile/FollowButton.css";
import "../../components/profile/BlockButton.css";
import "../../components/profile/MessageButton.css";
import "../../components/profile/FollowModal.css";
import { parseServerDate } from "../../utils/dateUtils";

// ── Enum of account states that require a visible profile banner ──
type ProfileBannerState = "FROZEN" | "SUSPENDED" | "BANNED" | "DELETION_PENDING" | "DELETED_SELF" | "DELETED" | "NONE";

function getProfileBannerState(profile: ProfileModel): ProfileBannerState {
  // profile.status is lifecycle (ACTIVE/FROZEN_BY_USER etc.)
  if (profile.status === "FROZEN_BY_USER") return "FROZEN";
  // Admin soft-deleted the account — show a clear "deleted by admin" banner
  if (profile.status === "DELETED_BY_ADMIN") return "DELETED";
  // Permanently banned by moderation. Unlike a suspension there is no term to
  // expire, so no date check is needed — the status alone is the whole story.
  if (profile.status === "BANNED") return "BANNED";
  // Suspended by moderation. The status column can lag behind reality between
  // scheduler runs, so a term that has already run out shows no banner at all —
  // the same rule the login path applies.
  if (profile.status === "SUSPENDED") {
    const end = profile.suspensionEndDate ? parseServerDate(profile.suspensionEndDate) : null;
    const expired = end !== null && !isNaN(end.getTime()) && end.getTime() <= Date.now();
    return expired ? "NONE" : "SUSPENDED";
  }
  // Checked BEFORE the pending state: an closed account is a finished deletion,
  // and its scheduledDeletionAt has been cleared, so the order only matters if a
  // future change ever leaves both set.
  if (profile.closedAt) return "DELETED_SELF";
  if (profile.scheduledDeletionAt) return "DELETION_PENDING";
  return "NONE";
}

interface ProfileStatusBannerProps {
  profile: ProfileModel;
  isOwner: boolean;
  isCoolingDown: boolean;
  cooldownLabel: string;
  onCancelDeletion: () => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
  locale: string;
}

/**
 * Renders the appropriate account-state banner (frozen / deletion-pending / nothing)
 * based on the profile's current status. Centralises all banner logic in one place
 * so adding a new state only requires updating getProfileBannerState() and this component.
 */
const ProfileStatusBanner: React.FC<ProfileStatusBannerProps> = ({
  profile, isOwner, isCoolingDown, cooldownLabel, onCancelDeletion, t, locale,
}) => {
  const state = getProfileBannerState(profile);

  // Removed by an admin. Dated the same way the ban notice below is, so a visitor
  // can tell a removal from last year apart from one from this morning — and so the
  // wording matches what the owner is told on the login screen.
  // deletedByAdminAt can be null on rows removed before that field was sent, hence
  // the undated fallback.
  if (state === "DELETED") {
    const deletedAt = profile.deletedByAdminAt ? parseServerDate(profile.deletedByAdminAt) : null;
    const deletedStr =
      deletedAt && !isNaN(deletedAt.getTime())
        ? deletedAt.toLocaleDateString(
            locale.startsWith("tr") ? "tr-TR" : "en-US",
            { day: "numeric", month: "long", year: "numeric" }
          )
        : null;
    return (
      <div className="profile-state-banner profile-state-banner--deleted">
        <span className="profile-state-banner__text">
          {deletedStr
            ? t("profile_deleted_by_admin_notice_on", { date: deletedStr })
            : t("profile_deleted_by_admin_notice")}
        </span>
      </div>
    );
  }

  // Permanently closed by its owner. The entries below are real and stay readable —
  // only the person behind them is gone, which is exactly what the wording says.
  if (state === "DELETED_SELF" && profile.closedAt) {
    const closedStr = parseServerDate(profile.closedAt).toLocaleDateString(
      locale.startsWith("tr") ? "tr-TR" : "en-US",
      { day: "numeric", month: "long", year: "numeric" }
    );
    return (
      <div className="profile-state-banner profile-state-banner--deleted">
        <span className="profile-state-banner__text">
          {t("profile_deleted_by_self_notice", { date: closedStr })}
        </span>
      </div>
    );
  }

  // Permanently banned by moderation. Worded like the suspension notice below —
  // impersonal, no moderator named — and dated, so a visitor can tell a ban from
  // last year apart from one from this morning. Unlike the login message the banned
  // user themselves gets, it never carries the reason: that is between them and the
  // moderation team, not something every visitor reads off their profile.
  // bannedAt can be null on rows banned before that field was sent, hence the
  // undated fallback.
  if (state === "BANNED") {
    const bannedAt = profile.bannedAt ? parseServerDate(profile.bannedAt) : null;
    const bannedStr =
      bannedAt && !isNaN(bannedAt.getTime())
        ? bannedAt.toLocaleDateString(
            locale.startsWith("tr") ? "tr-TR" : "en-US",
            { day: "numeric", month: "long", year: "numeric" }
          )
        : null;
    return (
      <div className="profile-state-banner profile-state-banner--banned">
        <span className="profile-state-banner__text">
          {bannedStr
            ? t("profile_banned_notice", { date: bannedStr })
            : t("profile_banned_notice_no_date")}
        </span>
      </div>
    );
  }

  // Suspended by moderation. Deliberately worded impersonally and without naming
  // the moderator who acted: the notice tells a visitor why the profile is quiet
  // and when it comes back, and nothing more. An open-ended suspension has no date
  // to show, hence the undated variant.
  if (state === "SUSPENDED") {
    const suspendedUntil = profile.suspensionEndDate
      ? parseServerDate(profile.suspensionEndDate)
      : null;
    const untilStr =
      suspendedUntil && !isNaN(suspendedUntil.getTime())
        // Date AND time, unlike the sibling banners: the status-dot tooltip next to
        // it already shows the exact hour, and two different-looking end times on
        // the same screen read like a bug.
        ? suspendedUntil.toLocaleString(
            locale.startsWith("tr") ? "tr-TR" : "en-US",
            {
              day: "numeric", month: "long", year: "numeric",
              hour: "2-digit", minute: "2-digit",
            }
          )
        : null;
    return (
      <div className="profile-state-banner profile-state-banner--suspended">
        <span className="profile-state-banner__text">
          {untilStr
            ? t("profile_suspended_notice", { date: untilStr })
            : t("profile_suspended_notice_no_date")}
        </span>
      </div>
    );
  }

  // Frozen by its owner. Worded like the self-closure notice above — same actor,
  // same voluntary act — and dated, so a visitor can tell a freeze from last week
  // apart from one from an hour ago. freezeRequestedAt can be null on rows frozen
  // before that column existed, hence the undated fallback.
  if (state === "FROZEN") {
    const frozenStr = profile.freezeRequestedAt
      ? parseServerDate(profile.freezeRequestedAt).toLocaleDateString(
          locale.startsWith("tr") ? "tr-TR" : "en-US",
          { day: "numeric", month: "long", year: "numeric" }
        )
      : null;
    return (
      <div className="profile-state-banner profile-state-banner--frozen">
        <span className="profile-state-banner__text">
          {frozenStr
            ? t("profile_frozen_notice", { date: frozenStr })
            : t("profile_frozen_notice_no_date")}
        </span>
      </div>
    );
  }

  if (state === "DELETION_PENDING" && profile.scheduledDeletionAt) {
    const dateStr = parseServerDate(profile.scheduledDeletionAt).toLocaleDateString(
      locale.startsWith("tr") ? "tr-TR" : "en-US",
      { day: "numeric", month: "long", year: "numeric" }
    );
    return (
      <div className="profile-state-banner profile-state-banner--deletion">
        <span className="profile-state-banner__text">
          {t("profile_deletion_pending", { date: dateStr })}
        </span>
        {/* Cancel button / cooldown — only visible to the profile owner */}
        {isOwner && (
          isCoolingDown ? (
            <span className="profile-state-banner__locked">
              {t("account_cancel_cooldown_remaining", { time: cooldownLabel })}
            </span>
          ) : (
            <button
              className="profile-state-banner__cancel"
              onClick={onCancelDeletion}
              type="button"
            >
              {t("profile_deletion_cancel")}
            </button>
          )
        )}
      </div>
    );
  }

  return null;
};

// ── Admin bio moderation modal types ──
type AdminBioModal = "edit" | "delete" | "block" | "unblock" | null;

interface ProfilePageProps {
  userId: number;
}

// Main tab type. "album" sits between the two entry tabs and lists the images
// the user attached to their entries; it has no sub-tabs of its own.
type MainTab = "general" | "album" | "private";

const ENTRIES_PER_PAGE = 10;

export const ProfilePage: React.FC<ProfilePageProps> = ({ userId }) => {
  const { t, i18n } = useTranslation();
  const { user: currentUser, updateUserStatus, updateUserAvatar } = useAuth();
  const [profile, setProfile] = useState<ProfileModel | null>(null);
  // Follow modal open state + active tab
  const [followModal, setFollowModal] = useState<{ open: boolean; tab: FollowModalTab }>({
    open: false,
    tab: "followers",
  });

  // Block state — initialized from profile response, updated after block/unblock
  const [isBlocked, setIsBlocked] = useState(false);
  const [hasBlockedMe, setHasBlockedMe] = useState(false);

  // Following list for the "takip ettiklerim" private sub-tab
  const [followingList, setFollowingList] = useState<FollowUserModel[]>([]);
  const [followingLoading, setFollowingLoading] = useState(false);
  const [followingPage, setFollowingPage] = useState(0);
  const [followingTotalPages, setFollowingTotalPages] = useState(0);
  const [followingTotalItems, setFollowingTotalItems] = useState(0);
  const [followingTogglingIds, setFollowingTogglingIds] = useState<Set<number>>(new Set());

  // Blocked list for the "engellediklerim" private sub-tab
  const [blockedList, setBlockedList] = useState<BlockedUserItem[]>([]);
  const [blockedLoading, setBlockedLoading] = useState(false);
  const [blockedPage, setBlockedPage] = useState(0);
  const [blockedTotalPages, setBlockedTotalPages] = useState(0);
  const [blockedTotalItems, setBlockedTotalItems] = useState(0);
  const [unblockingIds, setUnblockingIds] = useState<Set<number>>(new Set());

  // Topic suggestion lists — shared state for both the public "approvedTopics"
  // sub-tab (Genel Bilgiler) and the owner-only "rejectedTopics" sub-tab (Bana Özel).
  // Only one of the two tabs can be active at a time, so a single list is enough.
  const [suggestionList, setSuggestionList] = useState<UserTopicSuggestionItem[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestionPage, setSuggestionPage] = useState(1);
  const [suggestionTotalPages, setSuggestionTotalPages] = useState(0);
  const [suggestionTotalItems, setSuggestionTotalItems] = useState(0);

  // Compute cooldown at component level (hook rules: no conditionals)
  // Append "Z" so the browser treats the server timestamp as UTC, not local time
  const cancelableAt = profile?.deletionRequestedAt
    ? new Date(parseServerDate(profile.deletionRequestedAt).getTime() + ACCOUNT_DELETION_CANCEL_COOLDOWN_HOURS * 3600000)
    : null;
  const { isCoolingDown, remainingLabel } = useCooldownTimer(cancelableAt);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check if viewing own profile
  const isOwnProfile = currentUser?.id === userId;

  // Admin or mod can force-remove any user's pin and moderate bio
  const isAdminOrMod = currentUser?.role === "ADMIN" || currentUser?.role === "MODERATOR";

  // ── Rookie approval rank ("Çaylak Onay Sırası") ──────────────────────
  // Shown ONLY to the rookie on their OWN profile — never to visitors.
  // The backend endpoint only ever returns the caller's own rank, and
  // returns rank = null for non-rookies, so nothing renders after promotion.
  const [rookieRank, setRookieRank] = useState<RookieRankDto | null>(null);

  useEffect(() => {
    // Fetch whenever the owner views their own profile.
    // NOTE: deliberately NOT gated on currentUser.role here — the role string
    // casing is not consistent across auth flows (AdminPage uses toUpperCase()
    // defensively for the same reason). The backend already returns
    // rank = null for non-rookies, so the badge stays hidden for authors.
    if (!isOwnProfile) {
      setRookieRank(null);
      return;
    }
    let cancelled = false;
    getMyRookieRank()
      .then((rank) => {
        if (!cancelled) setRookieRank(rank);
      })
      .catch((err) => {
        // Rank badge is optional — fail silently and just hide it
        console.error("Failed to load rookie rank:", err);
        if (!cancelled) setRookieRank(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isOwnProfile]);

  // Main tab state
  const [mainTab, setMainTab] = useState<MainTab>("general");
  
  // Sub-tab states
  const [publicSubTab, setPublicSubTab] = useState<PublicProfileTab>("recent");
  const [privateSubTab, setPrivateSubTab] = useState<PrivateProfileTab>("favorites");
  
  // Entries state
  const [entries, setEntries] = useState<EntryModel[]>([]);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  // Issue C: pinned entry state — kept separate so pin/unpin updates it without reloading the full profile
  const [pinnedEntry, setPinnedEntry] = useState<EntryModel | null>(null);
  const [isRemovingPin, setIsRemovingPin] = useState(false);

  // Bio (personal message) state
  const [bio, setBio] = useState<string | null>(null);
  const [bioBlocked, setBioBlocked] = useState(false);
  const [bioBlockReason, setBioBlockReason] = useState<string | null>(null);
  const [isBioEditing, setIsBioEditing] = useState(false);
  const [bioInput, setBioInput] = useState("");
  const [isBioSaving, setIsBioSaving] = useState(false);
  const [bioError, setBioError] = useState<string | null>(null);

  // ── Admin bio moderation modal state ──
  const [adminBioModal, setAdminBioModal] = useState<AdminBioModal>(null);
  const [adminBioInput, setAdminBioInput] = useState("");
  const [adminBioBlockReason, setAdminBioBlockReason] = useState("");
  const [adminBioLoading, setAdminBioLoading] = useState(false);
  const [adminBioError, setAdminBioError] = useState<string | null>(null);

  // ── Avatar / banner upload state ──
  const [isAvatarUploading, setIsAvatarUploading] = useState(false);
  const [isBannerUploading, setIsBannerUploading] = useState(false);
  const [avatarUploadError, setAvatarUploadError] = useState<string | null>(null);
  const [bannerUploadError, setBannerUploadError] = useState<string | null>(null);

  // Confirmation prompt state: 'avatar' | 'banner' | null
  const [confirmDelete, setConfirmDelete] = useState<"avatar" | "banner" | null>(null);

  // Crop/reposition state — stores the visible percentage area chosen via ImageCropModal.
  // Format: { x, y, width, height } all in 0–100 (% of image). Null = no crop saved yet.
  const [coverCrop, setCoverCrop] = useState<CropArea | null>(null);
  const [avatarCrop, setAvatarCrop] = useState<CropArea | null>(null);

  // Which modal is currently open: 'cover' | 'avatar' | null
  const [cropModalMode, setCropModalMode] = useState<"cover" | "avatar" | null>(null);
  // Aspect ratio passed to the modal — computed from the cover element or 1 for avatar
  const [cropModalAspect, setCropModalAspect] = useState<number>(1);

  // coverRef is kept for reading the element's aspect ratio when opening the crop modal
  const coverRef = useRef<HTMLDivElement>(null);
  // avatarDivRef is kept so callers can reference the avatar container if needed
  const avatarDivRef = useRef<HTMLDivElement>(null);

  // Hidden file inputs — triggered programmatically via ref.click()
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);

  // Load profile
  useEffect(() => {
    const loadProfile = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchProfileById(userId);
        setProfile(data);
        setPinnedEntry(data.pinnedEntry);
        setBio(data.bio);
        setBioBlocked(data.bioBlocked ?? false);
        setBioBlockReason(data.bioBlockReason ?? null);
        setIsBlocked(data.isBlocked ?? false);
        setHasBlockedMe(data.hasBlockedMe ?? false);
        // Restore saved crop areas (stored as JSON by ImageCropModal).
        // Validate bounds before applying — discard any corrupt data saved by the old
        // bug (where the Cropper fired onCropComplete with out-of-bounds percentages).
        const isValidCrop = (c: unknown): c is CropArea =>
          !!c && typeof c === "object" &&
          typeof (c as CropArea).x === "number" && typeof (c as CropArea).y === "number" &&
          typeof (c as CropArea).width === "number" && typeof (c as CropArea).height === "number" &&
          (c as CropArea).x >= 0 && (c as CropArea).y >= 0 &&
          (c as CropArea).width > 0 && (c as CropArea).height > 0 &&
          (c as CropArea).x + (c as CropArea).width <= 100 &&
          (c as CropArea).y + (c as CropArea).height <= 100;

        try {
          const savedCover = localStorage.getItem(`cover_crop_${data.id}`);
          if (savedCover) {
            const parsed = JSON.parse(savedCover);
            if (isValidCrop(parsed)) {
              setCoverCrop(parsed);
            } else {
              localStorage.removeItem(`cover_crop_${data.id}`);
            }
          }
        } catch { /* ignore corrupt data */ }
        try {
          const savedAvatar = localStorage.getItem(`avatar_crop_${data.id}`);
          if (savedAvatar) {
            const parsed = JSON.parse(savedAvatar);
            if (isValidCrop(parsed)) {
              setAvatarCrop(parsed);
            } else {
              localStorage.removeItem(`avatar_crop_${data.id}`);
            }
          }
        } catch { /* ignore corrupt data */ }
      } catch (err) {
        setError(t("profile_error_loading"));
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [userId, t]);

  // Load entries based on current tab
  const loadEntries = useCallback(async (page: number) => {
    try {
      setEntriesLoading(true);
      
      let data;
      if (mainTab === "album") {
        // The album loads its own data inside ProfileAlbum — nothing to fetch here
        return;
      }
      if (mainTab === "general") {
        data = await fetchPublicProfileEntries(userId, publicSubTab, page, ENTRIES_PER_PAGE);
      } else {
        data = await fetchPrivateProfileEntries(userId, privateSubTab, page, ENTRIES_PER_PAGE);
      }
      
      setEntries(data.entries);
      setCurrentPage(data.currentPage);
      setTotalPages(data.totalPages);
      setTotalItems(data.totalItems);
    } catch (err) {
      console.error("Failed to load entries:", err);
      setEntries([]);
    } finally {
      setEntriesLoading(false);
    }
  }, [userId, mainTab, publicSubTab, privateSubTab]);

  // Load paginated list of users the owner is following
  const loadFollowingList = useCallback(async (pg: number) => {
    setFollowingLoading(true);
    try {
      // pg is 0-indexed for the API; Pagination component uses 1-indexed display
      const res = await fetchFollowing(userId, pg, 10);
      setFollowingList(res.content);
      setFollowingPage(pg);
      setFollowingTotalPages(res.totalPages);
      setFollowingTotalItems(res.totalItems);
    } catch {
      setFollowingList([]);
    } finally {
      setFollowingLoading(false);
    }
  }, [userId]);

  // Load paginated list of users the owner has blocked
  const loadBlockedList = useCallback(async (pg: number) => {
    setBlockedLoading(true);
    try {
      // pg is 0-indexed for the API; Pagination component uses 1-indexed display
      const res = await fetchBlockedList(pg, 10);
      setBlockedList(res.content);
      setBlockedPage(pg);
      setBlockedTotalPages(res.totalPages);
      setBlockedTotalItems(res.totalItems);
    } catch {
      setBlockedList([]);
    } finally {
      setBlockedLoading(false);
    }
  }, []);

  // Load paginated topic suggestion list (approved = public tab, rejected = owner-only tab)
  const loadSuggestions = useCallback(async (tab: "approved" | "rejected", pg: number) => {
    setSuggestionsLoading(true);
    try {
      // pg is 1-indexed here — fetchUserTopicSuggestions converts to 0-indexed for the API
      const res = await fetchUserTopicSuggestions(userId, tab, pg, ENTRIES_PER_PAGE);
      setSuggestionList(res.items);
      setSuggestionPage(res.currentPage);
      setSuggestionTotalPages(res.totalPages);
      setSuggestionTotalItems(res.totalItems);
    } catch (err) {
      // Log the reason: an empty list and a failed request look identical in the
      // UI otherwise ("henüz kabul edilmiş başlık önerisi yok")
      console.error("Topic suggestions could not be loaded:", err);
      setSuggestionList([]);
    } finally {
      setSuggestionsLoading(false);
    }
  }, [userId]);

  // Load entries on mount and when tabs change
  useEffect(() => {
    if (profile) {
      // Check if we're trying to access private tab without being the owner
      if (mainTab === "private" && !isOwnProfile) {
        setMainTab("general");
        return;
      }
      // Skip entry loading for the album tab — ProfileAlbum fetches its own page
      if (mainTab === "album") {
        setEntries([]);
        return;
      }
      // Skip entry loading for the following/blocked tabs — they have their own loaders
      if (mainTab === "private" && (privateSubTab === "following" || privateSubTab === "blocked")) {
        setEntries([]);
        return;
      }
      // Skip entry loading for the topic suggestion tabs — they have their own loader
      if (
        (mainTab === "general" && publicSubTab === "approvedTopics") ||
        (mainTab === "private" && privateSubTab === "rejectedTopics")
      ) {
        setEntries([]);
        return;
      }
      loadEntries(1);
    }
  }, [profile, mainTab, publicSubTab, privateSubTab, loadEntries, isOwnProfile]);

  // Load the topic suggestion list when one of the suggestion sub-tabs is activated
  useEffect(() => {
    if (!profile) return;
    const isApprovedTab = mainTab === "general" && publicSubTab === "approvedTopics";
    const isRejectedTab = mainTab === "private" && isOwnProfile && privateSubTab === "rejectedTopics";
    if (!isApprovedTab && !isRejectedTab) return;
    setSuggestionList([]);
    setSuggestionPage(1);
    loadSuggestions(isApprovedTab ? "approved" : "rejected", 1);
  }, [profile, mainTab, publicSubTab, privateSubTab, isOwnProfile, loadSuggestions]);

  // Load following/blocked lists when the owner switches to those private sub-tabs
  useEffect(() => {
    if (mainTab !== "private" || !isOwnProfile || !profile) return;
    if (privateSubTab === "following") {
      setFollowingList([]);
      setFollowingPage(0);
      loadFollowingList(0);
    } else if (privateSubTab === "blocked") {
      setBlockedList([]);
      setBlockedPage(0);
      loadBlockedList(0);
    }
  }, [mainTab, privateSubTab, isOwnProfile, profile, loadFollowingList, loadBlockedList]);

  // Handle main tab change
  const handleMainTabChange = (tab: MainTab) => {
    if (tab !== mainTab) {
      setMainTab(tab);
      setCurrentPage(1);
    }
  };

  // Handle public sub-tab change
  const handlePublicSubTabChange = (tab: PublicProfileTab) => {
    if (tab !== publicSubTab) {
      setPublicSubTab(tab);
      setCurrentPage(1);
    }
  };

  // Handle private sub-tab change
  const handlePrivateSubTabChange = (tab: PrivateProfileTab) => {
    if (tab !== privateSubTab) {
      setPrivateSubTab(tab);
      setCurrentPage(1);
    }
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    loadEntries(page);
  };

  // Handle vote update (optimistic UI)
  const handleVoteUpdate = (entryId: number, updates: Partial<EntryModel>) => {
    setEntries(prevEntries =>
      prevEntries.map(entry =>
        entry.id === entryId ? { ...entry, ...updates } : entry
      )
    );
  };

  // Issue C: update pinnedEntry state when user pins or unpins from anywhere on the page
  const handlePinChange = (newPinnedEntry: EntryModel | null) => {
    setPinnedEntry(newPinnedEntry);
  };

  // BUG FIX: the delete callback was never wired up here, so deleting an entry from
  // the profile left the row on screen until a manual refresh. Drop it from the list
  // (and from the pinned slot, if it was the pinned one) as soon as the delete succeeds.
  const handleEntryDeleted = (entryId: number) => {
    setEntries(prevEntries => prevEntries.filter(entry => entry.id !== entryId));
    setTotalItems(prevTotal => (prevTotal > 0 ? prevTotal - 1 : 0));
    setPinnedEntry(prev => (prev && prev.id === entryId ? null : prev));
  };

  // Issue C: sync vote updates made on the pinned entry card
  const handlePinnedEntryVoteUpdate = (entryId: number, updates: Partial<EntryModel>) => {
    if (pinnedEntry && pinnedEntry.id === entryId) {
      setPinnedEntry(prev => prev ? { ...prev, ...updates } : prev);
    }
  };

  // Issue C: admin/mod force-removes the pin from this profile
  const handleAdminRemovePin = async () => {
    if (isRemovingPin) return;
    setIsRemovingPin(true);
    try {
      await adminRemovePin(userId);
      setPinnedEntry(null);
    } catch (err) {
      console.error("Failed to remove pin:", err);
    } finally {
      setIsRemovingPin(false);
    }
  };

  // Unfollow a user from the "takip ettiklerim" private tab list
  const handleToggleFollowing = async (targetUser: FollowUserModel) => {
    if (followingTogglingIds.has(targetUser.userId)) return;
    setFollowingTogglingIds(prev => new Set(prev).add(targetUser.userId));
    const wasFollowing = targetUser.isFollowing;
    // Optimistic update
    setFollowingList(prev =>
      prev.map(u => u.userId === targetUser.userId ? { ...u, isFollowing: !wasFollowing } : u)
    );
    try {
      if (wasFollowing) {
        await unfollowUser(targetUser.userId);
        // Remove from list after successful unfollow
        setFollowingList(prev => prev.filter(u => u.userId !== targetUser.userId));
      } else {
        await followUser(targetUser.userId);
      }
    } catch {
      // Roll back optimistic update on error
      setFollowingList(prev =>
        prev.map(u => u.userId === targetUser.userId ? { ...u, isFollowing: wasFollowing } : u)
      );
    } finally {
      setFollowingTogglingIds(prev => {
        const next = new Set(prev);
        next.delete(targetUser.userId);
        return next;
      });
    }
  };

  // Unblock a user from the "engellediklerim" private tab list
  const handleUnblockFromProfile = async (targetUserId: number) => {
    if (unblockingIds.has(targetUserId)) return;
    setUnblockingIds(prev => new Set(prev).add(targetUserId));
    try {
      await unblockUser(targetUserId);
      setBlockedList(prev => prev.filter(u => u.userId !== targetUserId));
    } catch {
      // Silently ignore — user can retry
    } finally {
      setUnblockingIds(prev => {
        const next = new Set(prev);
        next.delete(targetUserId);
        return next;
      });
    }
  };

  // Open bio editor with current text pre-filled
  const handleBioEditOpen = () => {
    setBioInput(bio ?? "");
    setIsBioEditing(true);
  };

  // Cancel bio editing without saving
  const handleBioEditCancel = () => {
    setIsBioEditing(false);
    setBioInput("");
    setBioError(null);
  };

  // Save bio to backend then update local state
  const handleBioSave = async () => {
    if (isBioSaving) return;
    setBioError(null);
    setIsBioSaving(true);
    try {
      const trimmed = bioInput.trim() || null;
      await updateBio(trimmed);
      setBio(trimmed);
      setIsBioEditing(false);
    } catch (err: any) {
      const code: string = err?.message || "";
      // BIO_TOO_LONG:N — backend embeds the actual limit so we can show it
      if (code.startsWith("BIO_TOO_LONG:")) {
        const limit = code.split(":")[1];
        setBioError(t("profile_bio_too_long", { max: limit }));
      } else {
        const translated = t(code);
        setBioError(translated !== code ? translated : t("profile_bio_save_error"));
      }
    } finally {
      setIsBioSaving(false);
    }
  };

  // ── Avatar upload handlers ──

  /**
   * Triggered when the user selects a file from the hidden avatar file input.
   * Validates client-side before sending to avoid wasted round-trips.
   */
  const handleAvatarFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset the input value so the same file can be re-selected after an error
    e.target.value = "";

    setAvatarUploadError(null);
    setIsAvatarUploading(true);
    try {
      const newUrl = await uploadAvatar(file);
      setProfile(prev => prev ? { ...prev, avatarUrl: newUrl } : prev);
      // Sync new avatar URL to header via AuthContext
      updateUserAvatar(newUrl);
      // Clear the old crop so stale position data from the previous image is not applied to the new one
      if (profile) localStorage.removeItem(`avatar_crop_${profile.id}`);
      setAvatarCrop(null);
    } catch (err: any) {
      const code: string = err?.message || "";
      if (code === "INVALID_FILE_TYPE") {
        setAvatarUploadError(t("profile_upload_error_type"));
      } else if (code.startsWith("FILE_TOO_LARGE")) {
        setAvatarUploadError(t("profile_upload_error_size"));
      } else {
        setAvatarUploadError(t("profile_upload_error_generic"));
      }
    } finally {
      setIsAvatarUploading(false);
    }
  };

  /**
   * Deletes the current user's avatar from the server and clears it in UI.
   */
  // Called by the X button — shows confirmation prompt instead of deleting immediately
  const handleDeleteAvatarRequest = () => setConfirmDelete("avatar");

  const handleDeleteAvatar = async () => {
    setConfirmDelete(null);
    if (isAvatarUploading) return;
    setAvatarUploadError(null);
    setIsAvatarUploading(true);
    try {
      await deleteAvatar();
      setProfile(prev => prev ? { ...prev, avatarUrl: null } : prev);
      // Sync avatar removal to header via AuthContext
      updateUserAvatar(null);
      // Clear saved crop so stale crop data doesn't apply to a new avatar
      if (profile) localStorage.removeItem(`avatar_crop_${profile.id}`);
      setAvatarCrop(null);
    } catch {
      setAvatarUploadError(t("profile_upload_error_generic"));
    } finally {
      setIsAvatarUploading(false);
    }
  };

  // ── Banner upload handlers ──

  /**
   * Triggered when the user selects a file from the hidden banner file input.
   */
  const handleBannerFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    e.target.value = "";

    setBannerUploadError(null);
    setIsBannerUploading(true);
    try {
      const newUrl = await uploadBanner(file);
      setProfile(prev => prev ? { ...prev, coverImageUrl: newUrl } : prev);
      // Clear the old crop so stale position data from the previous image is not applied to the new one
      if (profile) localStorage.removeItem(`cover_crop_${profile.id}`);
      setCoverCrop(null);
    } catch (err: any) {
      const code: string = err?.message || "";
      if (code === "INVALID_FILE_TYPE") {
        setBannerUploadError(t("profile_upload_error_type"));
      } else if (code.startsWith("FILE_TOO_LARGE")) {
        setBannerUploadError(t("profile_upload_error_size"));
      } else {
        setBannerUploadError(t("profile_upload_error_generic"));
      }
    } finally {
      setIsBannerUploading(false);
    }
  };

  /**
   * Called by the X button — shows confirmation prompt instead of deleting immediately.
   */
  const handleDeleteBannerRequest = () => setConfirmDelete("banner");

  /**
   * Deletes the current user's banner from the server and clears it in UI.
   */
  const handleDeleteBanner = async () => {
    setConfirmDelete(null);
    if (isBannerUploading) return;
    setBannerUploadError(null);
    setIsBannerUploading(true);
    try {
      await deleteBanner();
      setProfile(prev => prev ? { ...prev, coverImageUrl: null } : prev);
      // Clear saved crop so stale crop data doesn't apply to a new banner
      if (profile) localStorage.removeItem(`cover_crop_${profile.id}`);
      setCoverCrop(null);
    } catch {
      setBannerUploadError(t("profile_upload_error_generic"));
    } finally {
      setIsBannerUploading(false);
    }
  };

  // ── Crop modal handlers ──

  /**
   * Open the crop/reposition modal for the cover banner.
   * Reads the cover element's current dimensions to compute the correct aspect ratio.
   */
  const handleOpenCoverCrop = () => {
    const el = coverRef.current;
    const aspect = el && el.clientHeight > 0
      ? el.clientWidth / el.clientHeight
      : 6.5;
    setCropModalAspect(aspect);
    setCropModalMode("cover");
  };

  /**
   * Open the crop/reposition modal for the avatar (always square aspect).
   */
  const handleOpenAvatarCrop = () => {
    setCropModalAspect(1);
    setCropModalMode("avatar");
  };

  /**
   * Called by ImageCropModal when the user clicks Save.
   * Persists the percentage-based crop area to state and localStorage.
   */
  const handleCropSave = (area: CropArea) => {
    if (!profile) return;
    if (cropModalMode === "cover") {
      setCoverCrop(area);
      localStorage.setItem(`cover_crop_${profile.id}`, JSON.stringify(area));
    } else if (cropModalMode === "avatar") {
      setAvatarCrop(area);
      localStorage.setItem(`avatar_crop_${profile.id}`, JSON.stringify(area));
    }
    setCropModalMode(null);
  };

  const handleCloseCropModal = () => setCropModalMode(null);

  /**
   * Derive CSS background-size and background-position from a stored CropArea.
   *
   * Math: given croppedArea percentages {x, y, width, height} (all 0–100):
   *   backgroundSize  = (100/width * 100)% × (100/height * 100)%
   *   backgroundPosition-X = x * 100 / (100 - width)   [= 50 when width == 100]
   *   backgroundPosition-Y = y * 100 / (100 - height)
   *
   * These values replicate exactly what react-easy-crop shows in the editor,
   * with no coordinate-system switching — so there is never a position jump
   * when the modal closes.
   */
  const getCropStyle = (area: CropArea | null): React.CSSProperties => {
    if (!area) {
      return { backgroundSize: "cover", backgroundPosition: "center" };
    }
    // Guard: if crop data is out of bounds (old corrupt save), fall back to cover
    if (
      area.x < 0 || area.y < 0 ||
      area.width <= 0 || area.height <= 0 ||
      area.x + area.width > 100 || area.y + area.height > 100
    ) {
      return { backgroundSize: "cover", backgroundPosition: "center" };
    }
    const scaleX = (100 / area.width) * 100;
    const scaleY = (100 / area.height) * 100;
    const posX = area.width < 100 ? (area.x * 100) / (100 - area.width) : 50;
    const posY = area.height < 100 ? (area.y * 100) / (100 - area.height) : 50;
    return {
      backgroundSize: `${scaleX}% ${scaleY}%`,
      backgroundPosition: `${posX}% ${posY}%`,
    };
  };



  // ── Admin bio moderation handlers ──

  const openAdminBioModal = (type: AdminBioModal) => {
    setAdminBioInput(bio ?? "");
    setAdminBioBlockReason("");
    setAdminBioError(null);
    setAdminBioModal(type);
  };

  const closeAdminBioModal = () => {
    setAdminBioModal(null);
    setAdminBioError(null);
  };

  // Admin edits the bio directly
  const handleAdminEditBio = async () => {
    if (adminBioLoading) return;
    setAdminBioLoading(true);
    setAdminBioError(null);
    try {
      await adminEditUserBio(userId, adminBioInput.trim());
      setBio(adminBioInput.trim() || null);
      closeAdminBioModal();
    } catch (err: any) {
      setAdminBioError(err?.message || t("admin_action_failed"));
    } finally {
      setAdminBioLoading(false);
    }
  };

  // Admin deletes (clears) the bio
  const handleAdminDeleteBio = async () => {
    if (adminBioLoading) return;
    setAdminBioLoading(true);
    setAdminBioError(null);
    try {
      await adminDeleteUserBio(userId);
      setBio(null);
      closeAdminBioModal();
    } catch (err: any) {
      setAdminBioError(err?.message || t("admin_action_failed"));
    } finally {
      setAdminBioLoading(false);
    }
  };

  // Admin blocks the user from editing their bio (also clears it)
  const handleAdminBlockBio = async () => {
    if (adminBioLoading) return;
    setAdminBioLoading(true);
    setAdminBioError(null);
    try {
      await adminBlockUserBio(userId, adminBioBlockReason.trim() || undefined);
      setBio(null);
      setBioBlocked(true);
      setBioBlockReason(adminBioBlockReason.trim() || null);
      closeAdminBioModal();
    } catch (err: any) {
      setAdminBioError(err?.message || t("admin_action_failed"));
    } finally {
      setAdminBioLoading(false);
    }
  };

  // Admin lifts the bio block
  const handleAdminUnblockBio = async () => {
    if (adminBioLoading) return;
    setAdminBioLoading(true);
    setAdminBioError(null);
    try {
      await adminUnblockUserBio(userId);
      setBioBlocked(false);
      setBioBlockReason(null);
      closeAdminBioModal();
    } catch (err: any) {
      setAdminBioError(err?.message || t("admin_action_failed"));
    } finally {
      setAdminBioLoading(false);
    }
  };

  // Get CSS class for user status — delegates to shared util
  const getStatusClass = (status: string): string => getStatusCssClass(status);

  // Get tooltip text for user status — delegates to shared util
  /**
   * Tooltip for the status dot. For a suspended account this adds how much of
   * the term is left and when it ends; a self-deletion that has already been
   * carried out (closedAt set) reads as closed rather than as still in
   * progress; every other status keeps the plain label.
   * Takes the fields as arguments rather than reading `profile` directly, because
   * `profile` is nullable until the fetch resolves — the call site in the JSX is
   * already past that guard.
   */
  const getStatusTooltipText = (
    status: string,
    suspensionEndDate: string | null,
    closedAt: string | null
  ): string => getStatusTooltip(status, suspensionEndDate, t, closedAt);

  // Format registration date
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("tr-TR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  // Check if current user is a ROOKIE - used for showing appropriate messages in private tabs
  const isCurrentUserRookie = currentUser?.role === "ROOKIE";

  // Get empty message based on current tab
  const getEmptyMessage = (): string => {
    if (mainTab === "general") {
      switch (publicSubTab) {
        case "recent":
          return t("profile_no_entries");
        case "mostLiked":
          return t("profile_no_popular_entries");
        case "mostDisliked":
          return t("profile_no_disliked_entries");
        case "mostFavorited":
          return t("profile_no_favorited_entries");
        case "approvedTopics":
          return t("profile_no_approved_topics");
        default:
          return t("profile_no_entries");
      }
    } else {
      // For private tabs, if user is ROOKIE, show the cannot vote message
      // since ROOKIEs cannot vote (+, -, or fav)
      if (isCurrentUserRookie) {
        switch (privateSubTab) {
          case "favorites":
          case "liked":
          case "disliked":
            return t("profile_rookie_cannot_vote");
          case "following":
            return t("profile_no_following");
          case "blocked":
            return t("profile_no_blocked");
          case "rejectedTopics":
            return t("profile_no_rejected_topics");
          default:
            return t("profile_rookie_cannot_vote");
        }
      }
      // Normal messages for non-ROOKIE users
      switch (privateSubTab) {
        case "favorites":
          return t("profile_no_my_favorites");
        case "liked":
          return t("profile_no_my_liked");
        case "disliked":
          return t("profile_no_my_disliked");
        case "following":
          return t("profile_no_following");
        case "blocked":
          return t("profile_no_blocked");
        case "rejectedTopics":
          return t("profile_no_rejected_topics");
        default:
          return t("profile_no_entries");
      }
    }
  };

  // Determine which vote badge to highlight based on the active public sub-tab
  const getVoteHighlight = (): VoteHighlight => {
    if (mainTab !== "general") return "none";
    switch (publicSubTab) {
      case "mostLiked":     return "plus";
      case "mostDisliked":  return "minus";
      case "mostFavorited": return "fav";
      default:              return "none";
    }
  };

  // ── Admin bio moderation modal renderer ──
  const renderAdminBioModal = () => {
    if (!adminBioModal) return null;

    let title = "";
    let body: React.ReactNode = null;
    let confirmLabel = "";
    let onConfirm: () => void = () => {};
    let confirmDanger = false;

    switch (adminBioModal) {
      case "edit":
        title = t("admin_bio_modal_edit_title", { username: profile?.username });
        confirmLabel = t("admin_bio_modal_edit_confirm");
        onConfirm = handleAdminEditBio;
        body = (
          <div className="admin-bio-modal-body">
            <label className="admin-bio-modal-label">{t("admin_bio_modal_edit_label")}</label>
            <textarea
              className="admin-bio-modal-textarea"
              value={adminBioInput}
              onChange={(e) => setAdminBioInput(e.target.value.slice(0, BIO_MAX_LENGTH))}
              rows={3}
              maxLength={BIO_MAX_LENGTH}
              autoFocus
            />
            <span className="admin-bio-modal-charcount">
              {adminBioInput.length}/{BIO_MAX_LENGTH}
            </span>
          </div>
        );
        break;

      case "delete":
        title = t("admin_bio_modal_delete_title", { username: profile?.username });
        confirmLabel = t("admin_bio_modal_delete_confirm");
        onConfirm = handleAdminDeleteBio;
        confirmDanger = true;
        body = (
          <p className="admin-bio-modal-desc">
            {t("admin_bio_modal_delete_desc", { username: profile?.username })}
          </p>
        );
        break;

      case "block":
        title = t("admin_bio_modal_block_title", { username: profile?.username });
        confirmLabel = t("admin_bio_modal_block_confirm");
        onConfirm = handleAdminBlockBio;
        confirmDanger = true;
        body = (
          <div className="admin-bio-modal-body">
            <p className="admin-bio-modal-desc">
              {t("admin_bio_modal_block_desc", { username: profile?.username })}
            </p>
            <label className="admin-bio-modal-label">{t("admin_bio_modal_block_reason_label")}</label>
            <input
              className="admin-bio-modal-input"
              type="text"
              value={adminBioBlockReason}
              onChange={(e) => setAdminBioBlockReason(e.target.value.slice(0, 200))}
              placeholder={t("admin_bio_modal_block_reason_placeholder")}
              autoFocus
            />
          </div>
        );
        break;

      case "unblock":
        title = t("admin_bio_modal_unblock_title", { username: profile?.username });
        confirmLabel = t("admin_bio_modal_unblock_confirm");
        onConfirm = handleAdminUnblockBio;
        body = (
          <p className="admin-bio-modal-desc">
            {t("admin_bio_modal_unblock_desc", { username: profile?.username })}
          </p>
        );
        break;

      default:
        return null;
    }

    return (
      <div className="admin-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeAdminBioModal(); }}>
        <div
          className="admin-modal admin-bio-modal"

        >
          <div className="admin-modal-header">
            <h3>{title}</h3>
            <button className="modal-close-btn" onClick={closeAdminBioModal} type="button">
              &times;
            </button>
          </div>
          <div className="admin-modal-body">
            {adminBioError && (
              <div className="modal-error">{adminBioError}</div>
            )}
            {body}
            <div className="admin-bio-modal-actions">
              <button
                className="modal-cancel-btn"
                onClick={closeAdminBioModal}
                type="button"
                disabled={adminBioLoading}
              >
                {t("admin_bio_modal_cancel")}
              </button>
              <button
                className={confirmDanger ? "modal-delete-btn" : "modal-save-btn"}
                onClick={onConfirm}
                type="button"
                disabled={adminBioLoading}
              >
                {adminBioLoading ? "..." : confirmLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="profile-loading">
        <SpinnerLoading />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="profile-error">
        <p>{error || t("profile_not_found")}</p>
      </div>
    );
  }

  return (
    <div className="profile-container">
      {/* Cover Photo Area
          When the user has a custom banner it is applied as an inline background-image,
          overriding the per-theme gradient defined in the CSS. */}
      <div
        className="profile-cover"
        ref={coverRef}
        style={profile.coverImageUrl
          ? { backgroundImage: `url(${profile.coverImageUrl})`, backgroundRepeat: "no-repeat", ...getCropStyle(coverCrop) }
          : undefined}
      >
        <div className="profile-cover-placeholder">
          {/* Placeholder pattern - can be replaced with actual image later */}
          <svg className="cover-pattern" viewBox="0 0 200 100" preserveAspectRatio="xMidYMid slice">
            <defs>
              <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                <circle cx="10" cy="10" r="1" fill="rgba(255,255,255,0.3)" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        {/* Banner edit/delete buttons — visible to the profile owner on hover */}
        {isOwnProfile && (
          <div className="profile-banner-actions">
            {isBannerUploading ? (
              <span className="profile-banner-actions__loading">...</span>
            ) : (
              <>
                <button
                  className="profile-banner-actions__btn"
                  onClick={() => bannerInputRef.current?.click()}
                  title={t("profile_change_banner")}
                  type="button"
                >
                  <RiCameraLine />
                </button>
                {profile.coverImageUrl && (
                  <button
                    className="profile-banner-actions__delete"
                    onClick={handleDeleteBannerRequest}
                    title={t("profile_delete_banner")}
                    type="button"
                  >
                    <RiCloseLine />
                  </button>
                )}
              </>
            )}
            {/* Hidden file input for banner selection */}
            <input
              ref={bannerInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              hidden
              onChange={handleBannerFileSelect}
            />
          </div>
        )}

        {/* Cover reposition button — opens ImageCropModal */}
        {isOwnProfile && profile.coverImageUrl && (
          <div className="profile-cover-reposition">
            <button
              type="button"
              className="profile-cover-reposition__btn"
              onClick={handleOpenCoverCrop}
              title={t("profile_cover_reposition")}
            >
              <RiDragMove2Line />
            </button>
          </div>
        )}

        {/* Profile Avatar - positioned at bottom center of cover */}
        <div className="profile-avatar-wrapper">
          <div
            className={`profile-avatar ${getStatusClass(profile.status)}`}
            ref={avatarDivRef}
            style={profile.avatarUrl
              ? { backgroundImage: `url(${profile.avatarUrl})`, backgroundRepeat: "no-repeat", ...getCropStyle(avatarCrop) }
              : undefined}
          >
            {/* SVG fallback — only shown when no avatar photo is uploaded */}
            {!profile.avatarUrl && (
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
              </svg>
            )}
          </div>

          {/* Avatar edit/delete buttons — visible to the profile owner on hover */}
          {isOwnProfile && (
            <div className="profile-avatar-actions">
              {isAvatarUploading ? (
                <span className="profile-avatar-actions__loading">...</span>
              ) : (
                <>
                  <button
                    className="profile-avatar-actions__camera"
                    onClick={() => avatarInputRef.current?.click()}
                    title={t("profile_change_avatar")}
                    type="button"
                  >
                    <RiCameraLine />
                  </button>
                  {profile.avatarUrl && (
                    <>
                      <button
                        className="profile-avatar-actions__reposition"
                        onClick={handleOpenAvatarCrop}
                        title={t("profile_cover_reposition")}
                        type="button"
                      >
                        <RiDragMove2Line />
                      </button>
                      <button
                        className="profile-avatar-actions__delete"
                        onClick={handleDeleteAvatarRequest}
                        title={t("profile_delete_avatar")}
                        type="button"
                      >
                        <RiCloseLine />
                      </button>
                    </>
                  )}
                </>
              )}
              {/* Hidden file input for avatar selection */}
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                hidden
                onChange={handleAvatarFileSelect}
              />
            </div>
          )}
        </div>
      </div>

      {/* ── Club color stripe ──────────────────────────────────────────────────
          Shown only when the user's primary club (display_order=1) has at least
          one color set. Segments are split equally across all non-null colors
          that belong to that primary club. */}
      {profile.favoriteClubs && profile.favoriteClubs.length > 0 && (() => {
        const primaryClub = profile.favoriteClubs[0];
        const colors = [primaryClub.color1, primaryClub.color2, primaryClub.color3].filter(Boolean) as string[];
        if (colors.length === 0) return null;
        return (
          <div className="profile-club-stripe" aria-hidden="true">
            {colors.map((color, i) => (
              <span
                key={i}
                className="profile-club-stripe__segment"
                style={{ backgroundColor: color, flex: 1 }}
              />
            ))}
          </div>
        );
      })()}

      {/* ── Club logos ─────────────────────────────────────────────────────────
          Shown below the color stripe (or directly below the cover when no
          colors are set). All favorite clubs are shown in display_order order. */}
      {profile.favoriteClubs && profile.favoriteClubs.length > 0 && (
        <div className="profile-club-logos">
          {profile.favoriteClubs.map((club, idx) => (
            <Link
              key={club.id}
              to={`/kulup/${club.slug}`}
              className={`profile-club-logos__item profile-club-logos__item--rank-${idx + 1}`}
              title={club.nameTr ?? club.nameEn ?? club.shortName}
            >
              {club.logoUrl ? (
                <img
                  src={club.logoUrl}
                  alt={club.shortName}
                  className="profile-club-logos__img"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                />
              ) : (
                /* Default shield icon when no club logo is available */
                <svg
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="profile-club-logos__img"
                  style={{ color: "var(--text-muted)", opacity: 0.4 }}
                >
                  <path d="M12 2L3 6v6c0 5.25 3.75 10.15 9 11.35C17.25 22.15 21 17.25 21 12V6L12 2z" />
                </svg>
              )}
            </Link>
          ))}
        </div>
      )}

      {/* Profile Info Section */}
      <div className="profile-info-section">

        {/* Status + role — top-right corner. Status is a small dot with a tooltip
            (not the old prominent "aktif" pill), role is a compact text badge. */}
        <div className="profile-corner-badges">
          <div className="profile-corner-badges-row">
            {/* Custom tooltip instead of the native title attribute: the dot sits
                in the top-RIGHT corner, so a browser tooltip opens off the edge
                of the card. This one is anchored to the dot's right edge and
                grows leftwards, mirroring .status-badge-tooltip in AdminUsers.css. */}
            <span className="profile-status-dot-wrap">
              <span
                className={`profile-status-dot ${getStatusClass(profile.status)}`}
                aria-label={getStatusTooltipText(profile.status, profile.suspensionEndDate, profile.closedAt)}
              />
              <span className="profile-status-tooltip" role="tooltip">
                {getStatusTooltipText(profile.status, profile.suspensionEndDate, profile.closedAt)}
              </span>
            </span>
            <span className={`profile-role-badge ${getRoleCssClass(profile.role)}`}>
              {t(getRoleI18nKey(profile.role))}
            </span>
          </div>

          {/* ── Rookie approval rank ("Çaylak Onay Sırası") — OWNER-ONLY ──
              Sits directly under the çaylak role badge and shows ONLY the
              number. rookieRank stays null for visitors and non-rookies,
              so this badge never renders for anyone else. The tooltip
              explains what the number means. */}
          {isOwnProfile && rookieRank?.rank != null && (
            <span
              className="profile-rookie-rank-badge"
              title={t("profile_rookie_rank_label")}
              aria-label={t("profile_rookie_rank_label")}
            >
              {rookieRank.rank}
            </span>
          )}

          {/* ── Rookie ranking progress — OWNER-ONLY ──
              Shown instead of the rank badge while the rookie is still below
              the entry threshold (backend sends rank = null + progress fields).
              Politely tells the owner how many entries remain until they enter
              the "Çaylak Onay Sırası". Never rendered for visitors. */}
          {isOwnProfile &&
            rookieRank?.rank == null &&
            rookieRank?.entriesRequired != null &&
            rookieRank?.entriesWritten != null && (
              <span
                className="profile-rookie-progress"
                title={t("profile_rookie_progress_tooltip", {
                  required: rookieRank.entriesRequired,
                })}
              >
                {t("profile_rookie_progress", {
                  remaining: Math.max(
                    0,
                    rookieRank.entriesRequired - rookieRank.entriesWritten
                  ),
                  written: rookieRank.entriesWritten,
                  required: rookieRank.entriesRequired,
                })}
              </span>
            )}
        </div>

        {/* Upload errors — placed inside the info section so the avatar circle (bottom: -50px on cover)
            does not overlap them. The section's padding-top: 60px already clears the avatar. */}
        {bannerUploadError && (
          <div className="profile-upload-error">{bannerUploadError}</div>
        )}
        {avatarUploadError && (
          <div className="profile-upload-error">{avatarUploadError}</div>
        )}

        {/* Unified account-state banners — driven by profile status */}
        <ProfileStatusBanner
          profile={profile}
          isOwner={!!(currentUser && currentUser.id === profile.id)}
          isCoolingDown={isCoolingDown}
          cooldownLabel={remainingLabel}
          onCancelDeletion={async () => {
            try {
              await cancelAccountDeletion();
              updateUserStatus("AUTHOR");
              setProfile((prev) =>
                prev ? { ...prev, scheduledDeletionAt: null, deletionRequestedAt: null } : prev
              );
            } catch {
              // Silently ignore — user can retry from Settings
            }
          }}
          t={t}
          locale={i18n.language}
        />

        {/* Username on its own line — long usernames
            (up to 50 chars, with spaces) wrap naturally without pushing
            the badges around. Verified tick + status badge sit on the
            row below. */}
        <div className="profile-name-block">
          <div className="profile-name-row">
            <h1 className="profile-username">{profile.username}</h1>
          </div>
          <div className="profile-badge-row">
            {/* Verified badge — granted by admin/mod via the admin user panel */}
            {profile.isVerified && (
              <span
                className="profile-verified-badge"
                title={t("profile_verified_badge", "Doğrulanmış hesap")}
                aria-label={t("profile_verified_badge", "Doğrulanmış hesap")}
              >
                <RiVerifiedBadgeFill />
              </span>
            )}
            {/* Nickaltı badge — public for everyone; links to the user's
                auto-created nickaltı topic at /gundem/{nickaltiSlug} */}
            {profile.nickaltiSlug && (
              <Link
                to={`/gundem/${profile.nickaltiSlug}`}
                className="profile-nickalti-badge"
                title={t("profile_nickalti_badge_tooltip", "Nickaltı başlığına git")}
                aria-label={t("profile_nickalti_badge_tooltip", "Nickaltı başlığına git")}
              >
                {t("profile_nickalti_badge", "nickaltı")}
              </Link>
            )}
          </div>
        </div>

        {/* Follow, Message and Block buttons — shown to authenticated users viewing
            another user's profile. Hidden on an closed account: there is no longer a
            person there to follow, block or message, and the backend would reject the
            calls anyway. */}
        {currentUser && currentUser.id !== profile.id && !profile.closedAt && (
          <div className="profile-follow-action">
            {/* Only show follow button when neither party has blocked the other */}
            {!isBlocked && !hasBlockedMe && (
              <FollowButton
                targetUserId={profile.id}
                initialIsFollowing={profile.isFollowing}
                onToggle={(nowFollowing: boolean) =>
                  setProfile((prev) =>
                    prev
                      ? {
                          ...prev,
                          isFollowing: nowFollowing,
                          followerCount: prev.followerCount + (nowFollowing ? 1 : -1),
                        }
                      : prev
                  )
                }
              />
            )}
            {/* Message button — gated purely on the backend's canMessage flag, which
                already accounts for the owner having messages switched off (privacy
                NOBODY / AUTHORS), for blocks in either direction, and for either
                account not being ACTIVE (suspended, banned, frozen, deleted).
                No button is shown when a message could not be delivered. */}
            {profile.canMessage && (
              <MessageButton
                targetUserId={profile.id}
                targetUsername={profile.username}
                targetUserRole={profile.role}
              />
            )}
            <BlockButton
              targetUserId={profile.id}
              initialIsBlocked={isBlocked}
              onToggle={async (nowBlocked: boolean) => {
                setIsBlocked(nowBlocked);
                // When blocking: also clear follow relationship (backend removes it)
                // and drop the message button — a block closes messaging both ways.
                if (nowBlocked) {
                  setProfile((prev) =>
                    prev ? { ...prev, isFollowing: false, canMessage: false, followerCount: Math.max(0, prev.followerCount - (prev.isFollowing ? 1 : 0)) } : prev
                  );
                } else {
                  // When unblocking we cannot infer messaging eligibility locally:
                  // the owner's privacy preference and account status still decide it.
                  // Re-read the flag from the backend so the button reappears only
                  // when a message would actually be accepted.
                  try {
                    const fresh = await fetchProfileById(userId);
                    setProfile((prev) => (prev ? { ...prev, canMessage: fresh.canMessage } : prev));
                  } catch {
                    // Leave the button hidden — a page refresh will resolve it
                  }
                }
              }}
            />
          </div>
        )}

        {/* "Bu kullanıcı sizi engelledi" banner — shown when the profile owner has blocked the viewer */}
        {currentUser && currentUser.id !== profile.id && hasBlockedMe && (
          <div className="profile-blocked-by-banner">
            <span className="profile-blocked-by-banner__text">
              {t("profile_blocked_by_user")}
            </span>
          </div>
        )}

        {/* Stats — one segmented bar instead of scattered pill chips.
            Cells show the value on top and the label underneath, separated by
            thin dividers, so all numbers scan as a single aligned unit. */}
        <div className="profile-stats">
          <div className="profile-stats-bar">
            <div className="profile-stat-cell">
              <span className={`stat-cell-value ${profile.karma >= 0 ? "karma-positive" : "karma-negative"}`}>
                {profile.karma}
              </span>
              <span className="stat-cell-label">{t("profile_stat_karma")}</span>
            </div>
            <div className="profile-stat-cell">
              <span className="stat-cell-value">{profile.entryCount}</span>
              <span className="stat-cell-label">{t("profile_stat_entries")}</span>
            </div>
            <button
              className="profile-stat-cell profile-stat-cell--clickable"
              onClick={() => setFollowModal({ open: true, tab: "followers" })}
            >
              <span className="stat-cell-value">{profile.followerCount}</span>
              <span className="stat-cell-label">{t("profile_stat_followers")}</span>
            </button>
            <button
              className="profile-stat-cell profile-stat-cell--clickable"
              onClick={() => setFollowModal({ open: true, tab: "following" })}
            >
              <span className="stat-cell-value">{profile.followingCount}</span>
              <span className="stat-cell-label">{t("profile_stat_following")}</span>
            </button>
            <div className="profile-stat-cell">
              <span className="stat-cell-value">{profile.daysLoggedIn}</span>
              <span className="stat-cell-label">{t("profile_stat_days_logged")}</span>
            </div>
            <div className="profile-stat-cell">
              <span className="stat-cell-value">{profile.consecutiveDays}</span>
              <span className="stat-cell-label">{t("profile_stat_consecutive")}</span>
            </div>
          </div>

          {/* ── Topic suggestion stats — grouped into their own smaller bar
              under the main one instead of three more loose chips.
              Approved count is public info (the approved list is public) — shown to
              visitors only when > 0 to avoid clutter, always shown to the owner.
              Total and rejected counts are private review outcomes — owner-only
              (the backend returns 0 for them to other viewers anyway). */}
          {(isOwnProfile || profile.topicSuggestionApprovedCount > 0) && (
            <div className="profile-stats-bar profile-topic-stats">
              {isOwnProfile && (
                <div className="profile-stat-cell">
                  <span className="stat-cell-value">{profile.topicSuggestionTotalCount}</span>
                  <span className="stat-cell-label">{t("profile_stat_topics_suggested")}</span>
                </div>
              )}
              <div className="profile-stat-cell">
                <span className="stat-cell-value">{profile.topicSuggestionApprovedCount}</span>
                <span className="stat-cell-label">{t("profile_stat_topics_approved")}</span>
              </div>
              {isOwnProfile && (
                <div className="profile-stat-cell">
                  <span className="stat-cell-value">{profile.topicSuggestionRejectedCount}</span>
                  <span className="stat-cell-label">{t("profile_stat_topics_rejected")}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Registration Date */}
        <div className="profile-meta">
          <span className="profile-registration-date">
            {t("profile_member_since", { date: formatDate(profile.registrationDate) })}
          </span>
        </div>

        {/* =====================================================================
            Bio / Personal Message
            - All visitors see the bio when set.
            - Owner sees edit button when bio exists, or an "add bio" prompt.
            - When bioBlocked is true AND the owner is viewing: show blocked warning
              instead of the editor so they know they cannot change it.
            - Admin/mod viewing someone else's profile: action toolbar appears
              with edit / delete / block / unblock buttons.
            ===================================================================== */}
        <div className="profile-bio">

          {/* ── Admin/mod toolbar — only visible when viewing someone else's profile ── */}
          {!isOwnProfile && isAdminOrMod && (
            <div className="profile-bio__admin-toolbar">
              <span className="profile-bio__admin-toolbar-label">
                {t("admin_bio_toolbar_label")}
              </span>
              <div className="profile-bio__admin-toolbar-actions">
                {/* Edit bio directly */}
                <button
                  className="profile-bio__admin-btn profile-bio__admin-btn--edit"
                  onClick={() => openAdminBioModal("edit")}
                  title={t("admin_bio_action_edit")}
                  type="button"
                >
                  <FaEdit />
                  <span>{t("admin_bio_action_edit")}</span>
                </button>

                {/* Delete (clear) bio — only shown when bio exists */}
                {bio && (
                  <button
                    className="profile-bio__admin-btn profile-bio__admin-btn--delete"
                    onClick={() => openAdminBioModal("delete")}
                    title={t("admin_bio_action_delete")}
                    type="button"
                  >
                    <FaTrash />
                    <span>{t("admin_bio_action_delete")}</span>
                  </button>
                )}

                {/* Block / unblock toggled by current block state */}
                {bioBlocked ? (
                  <button
                    className="profile-bio__admin-btn profile-bio__admin-btn--unblock"
                    onClick={() => openAdminBioModal("unblock")}
                    title={t("admin_bio_action_unblock")}
                    type="button"
                  >
                    <FaUndo />
                    <span>{t("admin_bio_action_unblock")}</span>
                  </button>
                ) : (
                  <button
                    className="profile-bio__admin-btn profile-bio__admin-btn--block"
                    onClick={() => openAdminBioModal("block")}
                    title={t("admin_bio_action_block")}
                    type="button"
                  >
                    <FaBan />
                    <span>{t("admin_bio_action_block")}</span>
                  </button>
                )}
              </div>
            </div>
          )}

          {isBioEditing ? (
            /* ── Edit mode — framed card with floating label ── */
            <div className="profile-bio__card">
              <span className="profile-bio__label">{t("profile_bio_label")}</span>
              <div className="profile-bio__editor">
                <textarea
                  className="profile-bio__textarea"
                  value={bioInput}
                  onChange={(e) => setBioInput(e.target.value.slice(0, BIO_MAX_LENGTH))}
                  placeholder={t("profile_bio_placeholder")}
                  maxLength={BIO_MAX_LENGTH}
                  rows={2}
                  autoFocus
                />
                {bioError && (
                  <p className="profile-bio__error">{bioError}</p>
                )}
                <div className="profile-bio__editor-footer">
                  <span className="profile-bio__char-count">
                    {bioInput.length}/{BIO_MAX_LENGTH}
                  </span>
                  <div className="profile-bio__editor-actions">
                    <button
                      className="profile-bio__btn profile-bio__btn--cancel"
                      onClick={handleBioEditCancel}
                      type="button"
                    >
                      {t("profile_bio_cancel")}
                    </button>
                    <button
                      className="profile-bio__btn profile-bio__btn--save"
                      onClick={handleBioSave}
                      disabled={isBioSaving}
                      type="button"
                    >
                      <RiCheckLine />
                      {isBioSaving ? "..." : t("profile_bio_save")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : bio ? (
            /* ── Display mode — framed card with floating label ── */
            <div className="profile-bio__card">
              <span className="profile-bio__label">{t("profile_bio_label")}</span>
              <div className="profile-bio__display">
                <p className="profile-bio__text">{bio}</p>
                {/* Edit button only for owner when bio is NOT blocked */}
                {isOwnProfile && !bioBlocked && (
                  <button
                    className="profile-bio__edit-btn"
                    onClick={handleBioEditOpen}
                    title={t("profile_bio_edit")}
                    type="button"
                  >
                    <RiEditLine />
                  </button>
                )}
              </div>
            </div>
          ) : isOwnProfile ? (
            bioBlocked ? (
              /* ── Owner sees blocked warning instead of the add prompt ── */
              <div className="profile-bio__blocked-notice">
                <FaBan className="profile-bio__blocked-icon" />
                <div className="profile-bio__blocked-text">
                  <span className="profile-bio__blocked-title">
                    {t("profile_bio_blocked_title")}
                  </span>
                  {bioBlockReason && (
                    <span className="profile-bio__blocked-reason">
                      {t("profile_bio_blocked_reason", { reason: bioBlockReason })}
                    </span>
                  )}
                </div>
              </div>
            ) : (
              /* ── Empty state — own profile, no bio yet ── */
              <button
                className="profile-bio__add-prompt"
                onClick={handleBioEditOpen}
                type="button"
              >
                <RiEditLine />
                <span>{t("profile_bio_add_prompt")}</span>
              </button>
            )
          ) : null}
        </div>

        {/* =====================================================================
            Issue C: Pinned Entry
            Shown directly below the registration date, above the main tabs.
            - All visitors see it.
            - Owner: the 3-dot menu inside EntryRow shows "Profilimden kaldır" (unpin).
            - Admin/Mod on someone else's profile: force-remove button (X) appears in the header.
            ===================================================================== */}
        {pinnedEntry && (
          <div className="profile-pinned-entry">

            {/* Header: pin icon + label + optional admin force-remove button */}
            <div className="profile-pinned-entry__header">
              <RiPushpinLine className="profile-pinned-entry__icon" />
              <span className="profile-pinned-entry__label">
                {t("profile_pinned_entry_label")}
              </span>

              {/* Admin/mod viewing someone else's profile — force-remove the pin */}
              {!isOwnProfile && isAdminOrMod && (
                <button
                  className="profile-pinned-entry__remove-btn"
                  title={t("profile_admin_remove_pin")}
                  onClick={handleAdminRemovePin}
                  disabled={isRemovingPin}
                >
                  <RiCloseLine />
                  <span>{isRemovingPin ? "..." : t("profile_admin_remove_pin")}</span>
                </button>
              )}
            </div>

            {/* Match title link above the entry card */}
            <Link
              to={generateMatchUrlFromEntry(
                pinnedEntry.homeTeam,
                pinnedEntry.awayTeam,
                pinnedEntry.matchDate,
                pinnedEntry.matchId
              )}
              className="profile-pinned-entry__match-link"
            >
              {pinnedEntry.homeTeam} - {pinnedEntry.awayTeam}
            </Link>

            {/* The entry itself rendered with EntryRow.
                isPinned=true on own profile so the menu toggles to "Profilimden kaldır".
                Visitors get isPinned=false — they don't see a pin toggle in their menu. */}
            <div className="profile-pinned-entry__card">
              <EntryRow
                entry={pinnedEntry}
                isPinned={isOwnProfile}
                onPinChange={handlePinChange}
                onVoteUpdate={handlePinnedEntryVoteUpdate}
                // Clear the pinned card immediately when its entry is deleted
                onDelete={handleEntryDeleted}
              />
            </div>

          </div>
        )}
      </div>

      {/* Main Tabs Section */}
      <div className="profile-main-tabs">
        <button
          className={`profile-main-tab ${mainTab === "general" ? "active" : ""}`}
          onClick={() => handleMainTabChange("general")}
        >
          {t("profile_tab_general")}
        </button>
        <button
          className={`profile-main-tab ${mainTab === "album" ? "active" : ""}`}
          onClick={() => handleMainTabChange("album")}
        >
          {t("profile_tab_album")}
        </button>
        {isOwnProfile && (
          <button
            className={`profile-main-tab ${mainTab === "private" ? "active" : ""}`}
            onClick={() => handleMainTabChange("private")}
          >
            {t("profile_tab_private")}
          </button>
        )}
      </div>

      {/* Sub-Tabs Section — the album tab has no sub-tabs, so the strip is
          not rendered at all while it is active */}
      {mainTab !== "album" && (
      <div className="profile-sub-tabs">
        {mainTab === "general" ? (
          <>
            <button
              className={`profile-sub-tab ${publicSubTab === "recent" ? "active" : ""}`}
              onClick={() => handlePublicSubTabChange("recent")}
            >
              {t("profile_subtab_recent")}
            </button>
            <button
              className={`profile-sub-tab ${publicSubTab === "mostLiked" ? "active" : ""}`}
              onClick={() => handlePublicSubTabChange("mostLiked")}
            >
              {t("profile_subtab_most_liked")}
            </button>
            <button
              className={`profile-sub-tab ${publicSubTab === "mostDisliked" ? "active" : ""}`}
              onClick={() => handlePublicSubTabChange("mostDisliked")}
            >
              {t("profile_subtab_most_disliked")}
            </button>
            <button
              className={`profile-sub-tab ${publicSubTab === "mostFavorited" ? "active" : ""}`}
              onClick={() => handlePublicSubTabChange("mostFavorited")}
            >
              {t("profile_subtab_most_favorited")}
            </button>
            <button
              className={`profile-sub-tab ${publicSubTab === "approvedTopics" ? "active" : ""}`}
              onClick={() => handlePublicSubTabChange("approvedTopics")}
            >
              {t("profile_subtab_approved_topics")}
            </button>
          </>
        ) : (
          <>
            <button
              className={`profile-sub-tab ${privateSubTab === "favorites" ? "active" : ""}`}
              onClick={() => handlePrivateSubTabChange("favorites")}
            >
              {t("profile_subtab_my_favorites")}
            </button>
            <button
              className={`profile-sub-tab ${privateSubTab === "liked" ? "active" : ""}`}
              onClick={() => handlePrivateSubTabChange("liked")}
            >
              {t("profile_subtab_my_liked")}
            </button>
            <button
              className={`profile-sub-tab ${privateSubTab === "disliked" ? "active" : ""}`}
              onClick={() => handlePrivateSubTabChange("disliked")}
            >
              {t("profile_subtab_my_disliked")}
            </button>
            <button
              className={`profile-sub-tab ${privateSubTab === "following" ? "active" : ""}`}
              onClick={() => handlePrivateSubTabChange("following")}
            >
              {t("profile_subtab_following")}
            </button>
            <button
              className={`profile-sub-tab ${privateSubTab === "blocked" ? "active" : ""}`}
              onClick={() => handlePrivateSubTabChange("blocked")}
            >
              {t("profile_subtab_blocked")}
            </button>
            <button
              className={`profile-sub-tab ${privateSubTab === "rejectedTopics" ? "active" : ""}`}
              onClick={() => handlePrivateSubTabChange("rejectedTopics")}
            >
              {t("profile_subtab_rejected_topics")}
            </button>
          </>
        )}
      </div>
      )}

      {/* Entries List */}
      <div className="profile-entries">
        {/* Album grid — images attached to the user's entries */}
        {mainTab === "album" ? (
          <ProfileAlbum userId={userId} />
        ) : /* Following list — "takip ettiklerim" private sub-tab */
        mainTab === "private" && privateSubTab === "following" ? (
          <div className="profile-user-list">
            {followingLoading && followingList.length === 0 ? (
              <div className="profile-entries-loading"><SpinnerLoading /></div>
            ) : followingList.length === 0 ? (
              <div className="profile-entries-empty"><p>{getEmptyMessage()}</p></div>
            ) : (
              <>
                {followingList.map(u => (
                  <div key={u.userId} className="profile-user-list__row">
                    <Link to={`/yazar/${u.username}--${u.userId}`} className="profile-user-list__username">
                      {u.username}
                    </Link>
                    <button
                      className="profile-user-list__action profile-user-list__action--unfollow"
                      onClick={() => handleToggleFollowing(u)}
                      disabled={followingTogglingIds.has(u.userId)}
                    >
                      {followingTogglingIds.has(u.userId) ? "..." : t("follow_unfollow")}
                    </button>
                  </div>
                ))}
                {followingLoading && <div className="profile-entries-loading"><SpinnerLoading /></div>}
                {followingTotalPages > 1 && (
                  <Pagination
                    currentPage={followingPage + 1}
                    totalPages={followingTotalPages}
                    totalItems={followingTotalItems}
                    paginate={(page) => loadFollowingList(page - 1)}
                  />
                )}
              </>
            )}
          </div>
        ) : mainTab === "private" && privateSubTab === "blocked" ? (
          /* Blocked list — "engellediklerim" private sub-tab */
          <div className="profile-user-list">
            {blockedLoading && blockedList.length === 0 ? (
              <div className="profile-entries-loading"><SpinnerLoading /></div>
            ) : blockedList.length === 0 ? (
              <div className="profile-entries-empty"><p>{getEmptyMessage()}</p></div>
            ) : (
              <>
                {blockedList.map(u => (
                  <div key={u.userId} className="profile-user-list__row">
                    <Link to={`/yazar/${u.username}--${u.userId}`} className="profile-user-list__username">
                      {u.username}
                    </Link>
                    <button
                      className="profile-user-list__action profile-user-list__action--unblock"
                      onClick={() => handleUnblockFromProfile(u.userId)}
                      disabled={unblockingIds.has(u.userId)}
                    >
                      {unblockingIds.has(u.userId) ? "..." : t("block_unblock")}
                    </button>
                  </div>
                ))}
                {blockedLoading && <div className="profile-entries-loading"><SpinnerLoading /></div>}
                {blockedTotalPages > 1 && (
                  <Pagination
                    currentPage={blockedPage + 1}
                    totalPages={blockedTotalPages}
                    totalItems={blockedTotalItems}
                    paginate={(page) => loadBlockedList(page - 1)}
                  />
                )}
              </>
            )}
          </div>
        ) : (mainTab === "general" && publicSubTab === "approvedTopics") ||
            (mainTab === "private" && privateSubTab === "rejectedTopics") ? (
          /* Topic suggestion lists — public "kabul edilen başlıklar" (Genel Bilgiler)
             and owner-only "reddedilen başlıklar" (Bana Özel) sub-tabs */
          <div className="profile-topic-suggestions">
            {suggestionsLoading ? (
              <div className="profile-entries-loading"><SpinnerLoading /></div>
            ) : suggestionList.length === 0 ? (
              <div className="profile-entries-empty"><p>{getEmptyMessage()}</p></div>
            ) : (
              <>
                {suggestionList.map((s) => (
                  <div key={s.id} className="profile-topic-suggestion__row">
                    <div className="profile-topic-suggestion__main">
                      {s.topicSlug ? (
                        /* Approved suggestion with a linked, active topic — link to its page */
                        <Link
                          to={`/gundem/${s.topicSlug}`}
                          className="profile-topic-suggestion__title"
                        >
                          {s.topicTitle ?? s.publishedTitle ?? s.title}
                        </Link>
                      ) : (
                        /* Rejected suggestion, or an approved one whose topic is no longer reachable */
                        <span className="profile-topic-suggestion__title profile-topic-suggestion__title--plain">
                          {s.publishedTitle ?? s.title}
                        </span>
                      )}
                      {s.categoryName && (
                        <span className="profile-topic-suggestion__category">
                          {s.categoryName}
                        </span>
                      )}
                    </div>

                    {/* Original suggested title — only shown when the admin edited it before publishing */}
                    {s.publishedTitle && s.publishedTitle !== s.title && (
                      <div className="profile-topic-suggestion__original">
                        {t("profile_topic_original_title", { title: s.title })}
                      </div>
                    )}

                    <div className="profile-topic-suggestion__meta">
                      <span>{t("profile_topic_suggested_at", { date: formatDate(s.createdAt) })}</span>
                      {s.reviewedAt && (
                        <span>{t("profile_topic_reviewed_at", { date: formatDate(s.reviewedAt) })}</span>
                      )}
                    </div>

                    {/* Rejection note from the reviewing admin/mod — rejected tab only */}
                    {s.status === "REJECTED" && (
                      <div className="profile-topic-suggestion__note">
                        <span className="profile-topic-suggestion__note-label">
                          {t("profile_topic_rejection_note_label")}:
                        </span>{" "}
                        <span className="profile-topic-suggestion__note-text">
                          {s.adminNote ?? t("profile_topic_no_note")}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
                {suggestionTotalPages > 1 && (
                  <Pagination
                    currentPage={suggestionPage}
                    totalPages={suggestionTotalPages}
                    totalItems={suggestionTotalItems}
                    paginate={(page) =>
                      loadSuggestions(
                        mainTab === "general" ? "approved" : "rejected",
                        page
                      )
                    }
                  />
                )}
              </>
            )}
          </div>
        ) : entriesLoading ? (
          <div className="profile-entries-loading">
            <SpinnerLoading />
          </div>
        ) : entries.length === 0 ? (
          <div className="profile-entries-empty">
            <p>{getEmptyMessage()}</p>
          </div>
        ) : (
          <>
            <div className="profile-entries-list">
              {entries.map((entry) => (
                <ProfileEntryAccordion 
                  key={entry.id} 
                  entry={entry} 
                  onVoteUpdate={handleVoteUpdate}
                  defaultExpanded={false}
                  // Issue C: pass pin props so "Profilime zımbala" / "Profilimden kaldır" works in the list
                  isPinned={isOwnProfile && pinnedEntry?.id === entry.id}
                  onPinChange={handlePinChange}
                  // Vote badges only in Genel Bilgiler, not in Bana Özel
                  showVoteBadges={mainTab === "general"}
                  highlightVote={getVoteHighlight()}
                  // Remove the row from the list as soon as the delete succeeds
                  onDelete={handleEntryDeleted}
                />
              ))}
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                paginate={handlePageChange}
              />
            )}
          </>
        )}
      </div>

      {/* Admin bio moderation modal — rendered via portal-like overlay */}
      {renderAdminBioModal()}

      {/* Follow modal: followers / following list overlay */}
      {profile && followModal.open && (
        <FollowModal
          profileUserId={profile.id}
          profileUsername={profile.username}
          initialTab={followModal.tab}
          onClose={() => setFollowModal((prev) => ({ ...prev, open: false }))}
        />
      )}
      {/* Delete confirmation modal — shown before permanently removing avatar or banner */}
      {confirmDelete && (
        <div className="icm-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="profile-delete-confirm" onClick={(e) => e.stopPropagation()}>
            <p className="profile-delete-confirm__msg">
              {confirmDelete === "avatar"
                ? t("profile_delete_avatar_confirm")
                : t("profile_delete_banner_confirm")}
            </p>
            <div className="profile-delete-confirm__actions">
              <button
                type="button"
                className="profile-delete-confirm__cancel"
                onClick={() => setConfirmDelete(null)}
              >
                {t("admin_cancel")}
              </button>
              <button
                type="button"
                className="profile-delete-confirm__ok"
                onClick={confirmDelete === "avatar" ? handleDeleteAvatar : handleDeleteBanner}
              >
                {t("profile_delete_confirm_ok")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image crop / reposition modal */}
      {cropModalMode && profile && (
        <ImageCropModal
          imageUrl={cropModalMode === "cover" ? profile.coverImageUrl! : profile.avatarUrl!}
          aspect={cropModalAspect}
          type={cropModalMode}
          // Pass the previously-saved crop so the modal restores the last position
          initialCroppedArea={cropModalMode === "cover" ? coverCrop : avatarCrop}
          onSave={handleCropSave}
          onClose={handleCloseCropModal}
        />
      )}
    </div>
  );
};